import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import styles from './Indicator.module.scss';

export interface IndicatorProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  variant?: 'accent' | 'neutral' | 'neutralTransparent' | 'light';
  size?: 300 | 400 | 450 | 500 | 600 | 700 | 800;
  value?: number;
  bgColor?: string;
  textColor?: string;
}

export const Indicator = ({
  variant = 'accent',
  size = 500,
  children,
  value = 0,
  bgColor,
  textColor,
  ...props
}: IndicatorProps) => {
  const prevValueRef = useRef(value);

  const [animationKey, setAnimationKey] = useState(0);

  const isMaxCount = value >= 100;

  useEffect(() => {
    if (!!value && !isMaxCount && value > prevValueRef.current) {
      setAnimationKey((prev) => prev + 1);

      prevValueRef.current = value;
    }
  }, [value]);

  return (
    <div
      className={styles.indicatorContainer}
      data-test-id="indicator-container"
    >
      {value > 0 && (
        <AnimatePresence>
          <motion.div
            key={animationKey}
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.25 }}
          >
            <div
              {...props}
              className={styles.indicator}
              data-variant={variant}
              data-size={size}
              data-test-id={`indicator-value-${variant}-${size}`}
              style={{
                ...props.style,
                background: bgColor,
                color: textColor,
                position: children ? 'absolute' : 'static',
              }}
            >
              {isMaxCount ? '99+' : value}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
      {children}
    </div>
  );
};
